/**
 * useNavState.js - Custom hook for navigation state and authentication status
 * 
 * This hook provides navigation state management and authentication status
 * for use in navigation components like Header and Footer.
 */
import { useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from './useAuth';

/**
 * Hook for managing navigation state and authentication status
 * 
 * @returns {Object} Navigation state and methods
 * @returns {Function} .navigateTo - Function to navigate to a route
 * @returns {boolean} .isAuthenticated - Whether user is authenticated
 * @returns {string} .currentPath - Current path
 * @returns {boolean} .isActive - Function to check if a path is active
 */
export const useNavState = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, logout } = useAuth();
  
  // Function to navigate to a route
  const navigateTo = useCallback((path) => {
    navigate(path);
  }, [navigate]);
  
  // Function to check if a path is active
  const isActive = useCallback((path) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  }, [location.pathname]);
  
  return {
    navigateTo,
    isAuthenticated,
    logout,
    currentPath: location.pathname,
    isActive
  };
};

export default useNavState;
